<template>
  <v-container fluid>
    <v-card outlined elevation="1">
      <!-- HEADER -->
      <v-toolbar dense flat color="primary" dark>
        <v-toolbar-title>Receipt Attachments</v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <!-- FORM -->
        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              :error-messages="errors.amount"
              dense
              outlined
              label="Amount"
              v-model="form.amount"
            />
          </v-col>

          <v-col cols="12" md="5">
            <v-text-field
              :error-messages="errors.notes"
              dense
              outlined
              label="Notes"
              v-model="form.notes"
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-file-input
              :error-messages="errors.image"
              dense
              outlined
              label="Image"
              v-model="form.image"
              accept="image/*"
            />
          </v-col>

          <v-col cols="12">
            <v-btn small color="green" outlined :loading="loadingSubmit" @click="submit">
              {{ form.id ? 'Update' : 'Add' }}
            </v-btn>

            <v-btn v-if="form.id" small text @click="resetForm">
              Cancel
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <!-- TABLE -->
        <v-data-table :headers="headers" :items="data" :loading="loading" dense disable-pagination>
          <!-- IMAGE -->
          <template v-slot:item.image="{ item }">
            <img
              :src="item.image_url_avatar"
              width="60"
              height="60"
              style="object-fit: cover; border-radius: 4px; cursor: pointer;"
              @click="showImage(item)"
            />
          </template>

          <!-- ACTIONS -->
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="edit(item)">
              <v-icon color="yellow darken-3">mdi-pencil</v-icon>
            </v-btn>

            <v-btn icon small color="red" @click="remove(item)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
      <v-dialog v-model="dialog" max-width="600">
        <v-card>
          <v-toolbar dense flat>
            <v-toolbar-title>Image Preview</v-toolbar-title>
            <v-spacer />
            <v-btn icon @click="dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="text-center">
            <img
              v-if="dialogData?.image_url_full"
              :src="dialogData?.image_url_full"
              style="max-width: 100%; max-height: 70vh; object-fit: contain;"
            />
            <!-- NOTES -->
            <div v-if="dialogData?.notes" class="mt-4 text-left">
              <v-divider class="mb-2"></v-divider>
              <div class="text-subtitle-2 font-weight-bold mb-1">Notes</div>
              <div class="text-body-2">{{ dialogData.notes }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-card>
  </v-container>
</template>

<script>
import appAxios from '@/libs/app-axios'
import { LoadingPopup, ErrorPopup, SuccessPopup } from '@/libs/utils'

export default {
  name: 'VoucherReceiptAttachments',

  data() {
    return {
      errors: {},
      data: [],
      loading: false,
      loadingSubmit: false,

      dialog: false,
      dialogData: {},

      form: {
        id: null,
        amount: null,
        notes: '',
        image: null,
      },

      headers: [
        { text: 'Image', value: 'image', sortable: false },
        { text: 'Amount', value: 'amount' },
        { text: 'Notes', value: 'notes' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
    }
  },
  computed: {
    voucherId() {
      return this.$route?.params?.voucherId
    },
  },
  mounted() {
    this.fetch()
  },

  methods: {
    showImage(item) {
      this.dialogData = item
      this.dialog = true
    },
    resetErrors() {
      this.errors = {}
    },
    handle422(error) {
      const response = error?.response
      if (response?.status === 422) {
        this.errors = response.data.errors || {}
        return true
      }
      return false
    },
    // ?? LIST
    async fetch() {
      try {
        this.loading = true

        const res = await appAxios().get(`/v3/vouchers/${this.voucherId}/receipt-attachments`)

        this.data = res.data.data
      } catch (e) {
        console.error(e)
        ErrorPopup.fire('Failed to load receipts')
      } finally {
        this.loading = false
      }
    },

    // ?? CREATE / UPDATE
    async submit() {
      try {
        this.loadingSubmit = true
        this.resetErrors()

        const fd = new FormData()
        fd.append('amount', this.form.amount)
        fd.append('notes', this.form.notes)

        if (this.form.image) {
          fd.append('image', this.form.image)
        }

        if (this.form.id) {
          await appAxios().post(`/v3/voucher-receipt-attachments/${this.form.id}?_method=PUT`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          SuccessPopup.fire('Updated')

          this.resetForm()
          this.fetch()
          return
        }
        await appAxios().post(`/v3/vouchers/${this.voucherId}/receipt-attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        SuccessPopup.fire('Created')
        this.resetForm()
        this.fetch()
      } catch (e) {
        // ? HANDLE VALIDATION
        if (this.handle422(e)) {
          return
        }

        console.error(e)
        ErrorPopup.fire('Failed to save')
      } finally {
        this.loadingSubmit = false
      }
    },

    // ?? EDIT
    edit(item) {
      this.form.id = item.id
      this.form.amount = item.amount
      this.form.notes = item.notes
      this.form.image = null
    },

    resetForm() {
      this.form = {
        id: null,
        amount: null,
        notes: '',
        image: null,
      }
    },

    // ?? DELETE
    async remove(item) {
      try {
        const confirmText = `DELETE ${item.id}`

        const input = prompt(`Type:\n${confirmText}\n\nto confirm deletion`)

        if (!input || input.trim() !== confirmText) {
          ErrorPopup.fire('Cancelled')
          return
        }

        LoadingPopup.fire('Deleting...')

        await appAxios().delete(`/v3/voucher-receipt-attachments/${item.id}`)

        LoadingPopup.close()
        SuccessPopup.fire('Deleted')

        this.fetch()
      } catch (e) {
        LoadingPopup.close()
        console.error(e)
        ErrorPopup.fire('Delete failed')
      }
    },
  },
}
</script>
